import React from "react";
import Card from "./Card";

/**
 * Phase 2 — FilterRow
 *
 * Unified horizontal pill-filter row (replaces the inconsistent
 * "ALL" solid-black + outlined siblings pattern that appeared on
 * Inventory, Removals, and other list pages).
 *
 * Usage:
 *   <FilterRow
 *     items={[
 *       {value: "all",       label: "All",       count: 432},
 *       {value: "expired",   label: "Expired",   count: 77, tone: "expired"},
 *       {value: "urgent",    label: "Urgent",    count: 22, tone: "urgent"},
 *       {value: "attention", label: "Soon",      count: 18, tone: "soon"},
 *       {value: "fresh",     label: "Fresh",     count: 315, tone: "fresh"},
 *     ]}
 *     value={filter}
 *     onChange={setFilter}
 *   />
 *
 * Behavior:
 *   - Selected chip uses the bg-paper-sunk + inset shadow (pressed)
 *     style — feels physically depressed.
 *   - Unselected chips use shadow-rest (slight raise).
 *   - Horizontal scroll on overflow on mobile.
 *   - Same height, same radius, same typography. No mixed metaphors.
 */
const TONE_DOT = {
  expired: "bg-status-expired",
  urgent:  "bg-status-urgent",
  soon:    "bg-status-soon",
  fresh:   "bg-status-fresh",
  neutral: "bg-ink/40",
};

export default function FilterRow({
  items = [],
  value,
  onChange,
  className = "",
  "data-testid": testId = "filter-row",
}) {
  return (
    <div
      className={`flex items-center gap-2 overflow-x-auto -mx-1 px-1 py-2 ${className}`}
      data-testid={testId}
      style={{ scrollbarWidth: "none" }}
    >
      {items.map((it) => {
        const selected = it.value === value;
        const dotTone = TONE_DOT[it.tone] || TONE_DOT.neutral;
        return (
          <button
            key={it.value}
            type="button"
            onClick={() => onChange && onChange(it.value)}
            className={[
              "shrink-0 inline-flex items-center gap-2 h-9 px-3 rounded-chip transition-all duration-150 ease-out",
              "font-mono text-[11px] font-bold uppercase tracking-wider",
              "active:scale-[0.98]",
              selected
                ? "bg-paper-sunk text-ink shadow-press"
                : "bg-paper-2 text-ink-2 shadow-rest hover:shadow-raised",
            ].join(" ")}
            data-testid={`filter-chip-${it.value}`}
            aria-pressed={selected}
          >
            {it.tone && (
              <span
                aria-hidden="true"
                className={`inline-block w-1.5 h-1.5 rounded-full ${dotTone}`}
              />
            )}
            <span>{it.label}</span>
            {typeof it.count === "number" && (
              <span className="text-ink-3 font-normal">{it.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
